// ============================================================================
// Hooks TanStack Query — PLANIFICATION SALARIALE (L05) : bundle + 5 mutations
// ============================================================================
// Hook = TanStack Query SEULEMENT (regle parc). L'autorisation vit au SERVEUR
// (M-651/652/85/86, routes/planification.ts, merge 6a5a68dc) : le SPA consomme
// `viewer.canManage` pour les affordances, il ne DECIDE jamais (lecon GA-05). Un
// 403 serveur devient une redirection/refus, pas un crash.
//
// Le serveur EST FIGE — ce module ne fait que CONSOMMER le contrat de
// /api/planification (endpoints + DTO lus dans server/routes/planification.ts +
// server/repositories/compensation.ts, jamais devines). Les types ci-dessous
// MIROITENT les DTO serveur a l'identique.
//
// M-657 (anti-fuite masse) : le DTO porte le roster nominatif scoped (positions,
// PAS de taux horaire individuel) — legitime car le director a canReadCompensation
// sur chaque membre ; la frise `teamTimeline` = totaux annuels SEULS. Le client ne
// reconstruit aucun agregat par-membre : il consomme ce que le serveur a assemble.
//
// Adaptateur camelCase -> snake_case (SL-A, decision de transposition 1) : le DTO
// emet la grille en camelCase (ManagementGridClass : augGrillePct, echelons[{rate,
// pctFromPrev}], niveaux[{echelonMax}]) ; le moteur `lib/salary/*` consomme
// `SalaryClass` snake_case. `adaptGridClasses` fait le pont ICI (le serveur est
// fige) — la grille interactive (SL-B) consomme des `SalaryClass[]`.

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, apiFetch, ApiForbiddenError, ApiServerError } from '@gs/api-client';
import type { SalaryClass } from '../lib/types/firestore';
import type { SalaryTimeline } from '../lib/salary/progression';
import type { RealPayrollRow } from '../lib/salary/types';
import type { OvertimeRule } from '../lib/salary/special-hours';

// ─── Types — MIROIR EXACT des DTO serveur (server/repositories/compensation.ts) ─

/** Role metier L05 (etage 3). Le DTO ne sort que director|admin (M-651 filtre en amont). */
export type GestPlanningRole = 'admin' | 'manager' | 'director' | 'employee';

/** Equipe de la portee (ScopeTeam) — la liste EST deja la portee du viewer (M-652). */
export interface PlanningTeam {
  id: string;
  name: string;
  department: string | null;
  directorEmail: string | null;
  directorName: string | null;
}

/** Echelon d'une classe de grille (camelCase, vue gestion). */
export interface PlanningGridEchelon {
  echelon: number;
  rate: number;
  pctFromPrev: number | null;
  /**
   * PDR-PLN-04 / P-CE1 (GC-A, additif) : taux N-1 (`prev_year_rate`). Le moteur L05
   * ne le lit PAS pour la projection ; la rangee « Taux N-1 » de SalaryMasterGrid
   * (GC-B) le consomme via `adaptGridClasses`. `null` = pas de taux N-1 saisi.
   */
  prevYearRate: number | null;
}
/** Niveau d'une classe — echelonMax = plafond d'avancement (M-814). */
export interface PlanningGridNiveau {
  level: number;
  label: string;
  echelonMax: number | null;
  /** PDR-PLN-04 / P-CE1 (GC-A, additif) : criteres/roles du niveau (tooltip N{n}). */
  description: string | null;
}
/** Classe de grille complete (camelCase). `adaptGridClasses` la mappe en `SalaryClass`. */
export interface PlanningGridClass {
  id: string;
  label: string;
  sortOrder: number;
  augGrillePct: number | null;
  baseHoursPerWeek: number;
  weeksPerYear: number;
  echelons: PlanningGridEchelon[];
  niveaux: PlanningGridNiveau[];
  /** PDR-PLN-04 / P-CE1 (GC-A, additif) : description generale de la classe (ClassInfoButton). */
  description: string | null;
}

/** Membre positionne dans la grille (M-657 : PAS de taux horaire individuel serialise). */
export interface PlanningGridMember {
  email: string;
  name: string;
  avatar_url: string | null;
  class_id: string | null;
  echelon: number;
  niveau: number | null;
  active: boolean;
  offGrid: boolean;
  offGridReferenceEchelon: number | null;
}

/** Changement de scenario (snake_case — parite lib type ScenarioChange). */
export interface PlanningScenarioChange {
  email: string | null;
  name: string;
  virtual: boolean;
  from_class_id: string | null;
  from_echelon: number | null;
  to_class_id: string | null;
  to_echelon: number;
}

/** Scenario salarial (snake_case — parite lib type SalaryScenario). */
export interface PlanningScenario {
  id: string;
  team_id: string;
  team_name: string;
  director_email: string;
  year: number;
  status: 'draft' | 'submitted' | 'approved' | 'rejected';
  changes: PlanningScenarioChange[];
  note: string | null;
  created_at: string;
  updated_at: string;
  submitted_at: string | null;
  decided_at: string | null;
  decided_by_email: string | null;
  decision_note: string | null;
}

/** Position grille passee (snake_case — parite lib type GridPosition). */
export interface PlanningGridPosition {
  email: string;
  year: number;
  class_id: string;
  echelon: number;
  source: 'manual';
  note: string | null;
  entered_by_email: string;
}

/** Changement d'echelon (snake_case — parite lib type EchelonChange). */
export interface PlanningEchelonChange {
  from_echelon: number;
  to_echelon: number;
  effective_year: number | null;
  effective_date: string | null;
  reason: string;
  created_at: string;
}

/** Bundle complet de la page (1 requete SPA) — miroir de la reponse GET /. */
export interface PlanificationResponse {
  viewer: { role: GestPlanningRole; email: string; canManage: boolean };
  teams: PlanningTeam[];
  selectedTeamId: string | null;
  selectedTeamName?: string;
  year: number;
  gridYear: number;
  classes: PlanningGridClass[];
  gridYears: number[];
  members: PlanningGridMember[];
  realRows: RealPayrollRow[];
  realByEmail: Record<string, { realBase: number | null; extras: number }>;
  specialHoursByEmail: Record<string, number>;
  teamTimeline: SalaryTimeline;
  pastByEmail: Record<string, PlanningGridPosition[]>;
  historyByEmail: Record<string, PlanningEchelonChange[]>;
  draft: PlanningScenario | null;
  pending: PlanningScenario | null;
  lastDecided: PlanningScenario | null;
  /** ADR-088 regle 5 : la regle des heures supplementaires de l annee ; null sans heures speciales. */
  overtimeRule: OvertimeRule | null;
}

// ─── Adaptateur camelCase -> snake_case (decision de transposition 1) ──────────

/**
 * Grille DTO (camelCase, vue gestion) -> `SalaryClass` (snake_case) consomme par
 * `lib/salary/*` (scenario/positions/progression) ET SalaryMasterGrid (GC-B). Le
 * serveur est FIGE : le pont vit ICI. `rate_2025`/`description` sont desormais
 * portes par le DTO (GC-A, additif) — le moteur ne les lit pas pour la projection
 * (neutre pour positions/scenario/progression), mais la vue CE en a besoin (rangee
 * « Taux N-1 », tooltips/ClassInfoButton). `created_at`/`updated_at` restent hors
 * DTO (aucun consommateur) — '' fidele.
 */
export function adaptGridClasses(classes: PlanningGridClass[]): SalaryClass[] {
  return classes.map((c) => ({
    id: c.id,
    label: c.label,
    description: c.description ?? null,
    sort_order: c.sortOrder,
    aug_grille_pct: c.augGrillePct,
    base_hours_per_week: c.baseHoursPerWeek,
    weeks_per_year: c.weeksPerYear,
    echelons: c.echelons.map((e) => ({
      echelon: e.echelon,
      rate: e.rate,
      rate_2025: e.prevYearRate ?? null,
      pct_from_prev: e.pctFromPrev,
    })),
    niveaux: c.niveaux.map((n) => ({
      level: n.level,
      label: n.label,
      echelon_max: n.echelonMax,
      description: n.description ?? null,
    })),
    created_at: '',
    updated_at: '',
  }));
}

// ─── Query (bundle page) ────────────────────────────────────────────────────────

const PLANIF_ROOT_KEY = ['gest', 'planification'] as const;
const planificationKey = (teamId: string | null) => [...PLANIF_ROOT_KEY, teamId ?? 'default'] as const;

/**
 * F-601..F-610 : bundle de la page (1 requete). Le SERVEUR decide de l'equipe
 * selectionnee (repli 1re equipe de la portee si `team` hors portee — jamais une
 * fuite). 403/404 = etats stables (pas de retry) : le 403 (manager/employee)
 * remonte comme ApiForbiddenError, la page redirige (garde client M-651).
 */
export function usePlanification(teamId: string | null, enabled: boolean) {
  return useQuery({
    queryKey: planificationKey(teamId),
    queryFn: () =>
      api.get<PlanificationResponse>('/api/planification', teamId ? { team: teamId } : undefined),
    enabled,
    retry: false,
  });
}

// ─── Codes d'erreur metier (GA-21 : jamais un code brut a l'ecran) ───────────

/**
 * Code metier VERBATIM renvoye par le serveur (errorHandler `legacy` ->
 * `{ error, code, message }`), sinon retombe sur un code generique par statut.
 * Le serveur emet des codes `GEST_PLANIF_*` (internes, filtres ici) ; la banniere
 * consomme le code court par statut : forbidden / notfound / invalid / server.
 * Le SPA mappe ensuite sur `gest:planification.errors.<code>`.
 */
export function planificationErrorCode(err: unknown): string {
  const body =
    err instanceof ApiServerError || err instanceof ApiForbiddenError ? err.originalBody : undefined;
  const code = (body as { code?: unknown } | undefined)?.code;
  if (typeof code === 'string' && code.length > 0 && !code.startsWith('GEST_')) return code;
  if (err instanceof ApiForbiddenError) return 'forbidden';
  if (err instanceof ApiServerError) {
    if (err.status === 404) return 'notfound';
    if (err.status === 400) return 'invalid';
  }
  return 'server';
}

// ─── Mutations (5) ──────────────────────────────────────────────────────────────
// Chaque ecriture (sauf l'autosave silencieux F-613) invalide le bundle : le
// serveur re-assemble la grille/scenarios apres persistance. L'autosave NE
// re-fetch PAS (un refetch clobbererait les editions locales de la grille en
// cours — le board garde `lastSavedPayloadRef`, SL-B).

/** Persistance partagee (bouton explicite + autosave) — Zod bornes VERBATIM serveur. */
export interface ScenarioChangePayload {
  email: string | null;
  name: string;
  virtual: boolean;
  from_class_id: string | null;
  from_echelon: number | null;
  to_class_id: string | null;
  to_echelon: number;
}
export interface SaveScenarioPayload {
  team_id: string;
  year: number;
  note?: string | null;
  changes: ScenarioChangePayload[];
}
export interface PastPositionPayload {
  email: string;
  year: number;
  class_id: string;
  echelon: number;
  note?: string | null;
  team_id?: string;
}
export interface DeletePositionPayload {
  email: string;
  year: number;
  team_id?: string;
}

/** F-612 : enregistrement explicite d'un brouillon (bandeau ?ok=draft_saved). */
export function useSaveScenarioDraft() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: SaveScenarioPayload) =>
      api.post<{ ok: 'draft_saved'; scenarioId: string }>('/api/planification/scenarios/draft', payload),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: PLANIF_ROOT_KEY });
    },
  });
}

/** F-613 : autosave SILENCIEUX (debounce cote board) — PAS d'invalidation. */
export function useAutosaveScenario() {
  return useMutation({
    mutationFn: (payload: SaveScenarioPayload) =>
      api.post<{ ok: true; scenarioId: string; savedAt: string }>(
        '/api/planification/scenarios/autosave',
        payload,
      ),
  });
}

/** F-614 : soumission au CE (M-85 : director auteur ou admin) — verrouille la grille. */
export function useSubmitScenario() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (scenarioId: string) =>
      api.post<{ ok: 'submitted' }>(
        `/api/planification/scenarios/${encodeURIComponent(scenarioId)}/submit`,
        {},
      ),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: PLANIF_ROOT_KEY });
      void qc.invalidateQueries({ queryKey: ['gest', 'grille-ce'] });
    },
  });
}

/** F-615 : backfill d'une position grille passee (M-86 : director de la CIBLE). */
export function useSavePastPosition() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: PastPositionPayload) =>
      api.post<{ ok: 'position_saved' }>('/api/planification/positions', payload),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: PLANIF_ROOT_KEY });
    },
  });
}

/** F-616 : suppression d'une position grille passee (DELETE avec corps -> apiFetch). */
export function useDeletePastPosition() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: DeletePositionPayload) =>
      apiFetch<{ ok: 'position_deleted' }>('/api/planification/positions', {
        method: 'DELETE',
        body: payload,
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: PLANIF_ROOT_KEY });
    },
  });
}
